import { useState, useEffect, useMemo } from 'react'
import { CheckCircle, Loader, Circle, XCircle } from 'lucide-react'
import { PROVISION_MILESTONES, type MilestoneDefinition, type TProvisionLog } from '@/types'
import { formatDuration } from '@/lib/utils'

interface MilestoneProgressProps {
  logs: TProvisionLog[]
  startedAt: string | null | undefined
  completedAt?: string | null
  /** Which milestone definitions to display. Defaults to PROVISION_MILESTONES.
   *  Typed `readonly` so the `as const satisfies` catalog arrays are accepted. */
  milestoneDefs?: readonly MilestoneDefinition[]
}

interface MilestoneState {
  id: string
  label: string
  order: number
  status: 'complete' | 'in_progress' | 'pending' | 'error'
  startTime?: string
  endTime?: string
  deltaMs?: number
}

export function MilestoneProgress({ logs, startedAt, completedAt, milestoneDefs }: MilestoneProgressProps) {
  const [tick, setTick] = useState(0)

  useEffect(() => {
    if (completedAt) return
    if (!startedAt) return
    const interval = setInterval(() => setTick(t => t + 1), 1000)
    return () => clearInterval(interval)
  }, [startedAt, completedAt])

  const milestones = useMemo<MilestoneState[]>(() => {
    const milestoneLogs = logs.filter(l => l.level === 'milestone' && l.milestone)
    const hasError = logs.some(l => l.level === 'error' && l.message?.toLowerCase().includes('failed'))
    const isRunning = !completedAt && !!startedAt

    const defs = milestoneDefs ?? PROVISION_MILESTONES
    const result = defs.map(def => {
      const matchingLogs = milestoneLogs.filter(l => l.milestone === def.id)
      const started = matchingLogs.find(l => l.milestone_started)
      const ended = matchingLogs.find(l => l.milestone_ended)
      let status: MilestoneState['status'] = 'pending'
      if (ended?.milestone_ended) status = 'complete'
      else if (started?.milestone_started) status = hasError ? 'error' : 'in_progress'
      return {
        id: def.id, label: def.label, order: def.order, status,
        startTime: started?.milestone_started ?? undefined,
        endTime: ended?.milestone_ended ?? undefined,
        deltaMs: ended?.milestone_delta_ms ?? undefined,
      }
    })

    // While provisioning is running, the first pending milestone after the last
    // completed one should show as in_progress (spinning). This handles the case
    // where milestones complete faster than the UI polls.
    if (isRunning && !hasError) {
      const lastCompleteIdx = result.reduce((acc, m, i) => m.status === 'complete' ? i : acc, -1)
      const nextPending = result.find((m, i) => i > lastCompleteIdx && m.status === 'pending')
      if (nextPending) nextPending.status = 'in_progress'
    }

    // If there's an error, mark the first in_progress or first pending after last complete as error
    if (hasError) {
      const inProgress = result.find(m => m.status === 'in_progress')
      if (inProgress) {
        inProgress.status = 'error'
      } else {
        const lastCompleteIdx = result.reduce((acc, m, i) => m.status === 'complete' ? i : acc, -1)
        const nextPending = result.find((m, i) => i > lastCompleteIdx && m.status === 'pending')
        if (nextPending) nextPending.status = 'error'
      }
    }

    return result
  }, [logs, startedAt, completedAt])

  const completedCount = milestones.filter(m => m.status === 'complete').length
  const totalCount = milestones.length
  const progressPercent = totalCount > 0 ? (completedCount / totalCount) * 100 : 0
  const isRunning = !completedAt && !!startedAt

  // Recompute on every tick (tick changes every second via setInterval)
  const elapsedMs = useMemo(() => {
    void tick // force dependency on tick for recomputation
    if (!startedAt) return 0
    const start = new Date(startedAt.replace(' ', 'T')).getTime()
    if (completedAt) return new Date(completedAt.replace(' ', 'T')).getTime() - start
    return Date.now() - start
  }, [startedAt, completedAt, tick])

  function formatTime(ts: string | undefined): string {
    if (!ts) return '--:--'
    try { return new Date(ts.replace(' ', 'T')).toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' }) }
    catch { return '--:--' }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
          <span style={{ fontSize: '13px', fontWeight: 500, color: 'var(--text-primary)' }}>{completedCount} of {totalCount} milestones</span>
          <span style={{ fontSize: '12px', fontFamily: 'var(--font-mono)', color: isRunning ? 'var(--status-provisioning)' : 'var(--text-secondary)' }}>
            {isRunning ? 'Elapsed: ' : completedAt ? 'Total: ' : ''}{formatDuration(elapsedMs)}
          </span>
        </div>
        <div style={{ height: '6px', backgroundColor: 'var(--bg-tertiary)', borderRadius: 'var(--radius-full)', overflow: 'hidden' }}>
          <div style={{ width: `${progressPercent}%`, height: '100%', backgroundColor: completedCount === totalCount ? 'var(--status-healthy)' : 'var(--status-provisioning)', borderRadius: 'var(--radius-full)', transition: 'width 500ms ease' }} />
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', maxHeight: '200px', overflowY: 'auto' }}>
        {milestones.map(m => {
          const icon = m.status === 'complete' ? <CheckCircle size={14} style={{ color: 'var(--status-healthy)' }} />
            : m.status === 'in_progress' ? <Loader size={14} style={{ color: 'var(--status-provisioning)', animation: 'spin 1s linear infinite' }} />
            : m.status === 'error' ? <XCircle size={14} style={{ color: 'var(--status-unhealthy)' }} />
            : <Circle size={14} style={{ color: 'var(--text-tertiary)', opacity: 0.4 }} />

          return (
            <div key={m.id} style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              padding: '3px 6px', borderRadius: 'var(--radius-sm)',
              backgroundColor: m.status === 'in_progress' ? 'var(--status-provisioning-dim)' : m.status === 'error' ? 'var(--status-unhealthy-dim)' : 'transparent',
            }}>
              <span style={{ flexShrink: 0, display: 'flex' }}>{icon}</span>
              <span style={{
                flex: 1, fontSize: '12px',
                fontWeight: m.status === 'in_progress' || m.status === 'error' ? 500 : 400,
                color: m.status === 'pending' ? 'var(--text-tertiary)' : m.status === 'error' ? 'var(--status-unhealthy)' : 'var(--text-primary)',
              }}>{m.label}</span>
              {m.deltaMs != null && m.deltaMs > 0 && (
                <span style={{ fontSize: '10px', fontFamily: 'var(--font-mono)', color: 'var(--text-tertiary)', flexShrink: 0 }}>{formatDuration(m.deltaMs)}</span>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
